import LoadingCircle from 'components/LoadingCircle';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAuthUser } from './AuthUserContext';
import { useCreations } from 'contexts/useCreations';
import useCreationCreate from 'hooks/useCreationCreate';
import { ShapesEnum } from 'enums';

// Custom hook only for my-creations page
const useMyCreationsLogic = () => {
  const [allCreations, refetch] = useCreations();
  const { createCreationFromShape } = useCreationCreate();
  const [isNavigating, setIsNavigating] = useState(false);
  const { isLoading, isAuthenticated, user } = useAuthUser();
  const [skipCreation, setSkipCreation] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const journalRegistration = sessionStorage.getItem('fromJournalRegistration');
      if (journalRegistration) {
        setIsNavigating(false);
        setSkipCreation(true);
      } else {
        setSkipCreation(false);
      }
    }
  }, []);

  useEffect(() => {
    const handleMyCreations = async () => {
      setIsNavigating(true);
      try {
        if (allCreations.length === 0) {
          await createCreationFromShape(ShapesEnum.ABSTRACT, null, null, true);
          await refetch();
        }
      } finally {
        setIsNavigating(false);
      }
    };

    const shouldCreate =
      !isLoading &&
      isAuthenticated &&
      allCreations.length === 0 &&
      user?.tp_user === 'T' &&
      !skipCreation &&
      !isNavigating;

    if (shouldCreate) {
      handleMyCreations();
    }
  }, [
    isLoading,
    isAuthenticated,
    allCreations,
    user,
    skipCreation,
    isNavigating,
    createCreationFromShape,
    refetch,
  ]);

  return { isNavigating };
};

/**
 * High Order Component to protect routes
 */
export const withAuth = (WrappedComponent) => {
  const GenericAuth = (props) => {
    const { isAuthenticated, isLoading, login } = useAuthUser();
    const router = useRouter();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        if (!router.pathname.includes('workspace')) {
          router.push('/');
        } else {
          login({ redirectPage: '/my-creations' });
        }
      }
    }, [isLoading, isAuthenticated, router, login]);

    if (isLoading || (!isLoading && !isAuthenticated)) {
      return <LoadingCircle />;
    }

    return <WrappedComponent {...props} />;
  };

  const MyCreationsAuth = (props) => {
    const { isAuthenticated, isLoading, login } = useAuthUser();
    const router = useRouter();
    const { isNavigating } = useMyCreationsLogic();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        // Original behavior: check workspace for my-creations too
        if (!router.pathname.includes('workspace')) {
          router.push('/');
        } else {
          login({ redirectPage: '/my-creations' });
        }
      }
    }, [isLoading, isAuthenticated, router, login]);

    if (isLoading || (!isLoading && !isAuthenticated) || isNavigating) {
      return <LoadingCircle />;
    }

    return <WrappedComponent {...props} />;
  };

  const WithAuthComponent = (props) => {
    const router = useRouter();
    const isMyCreationsPage = router.pathname.includes('my-creations');
    return isMyCreationsPage ? <MyCreationsAuth {...props} /> : <GenericAuth {...props} />;
  };

  if (WrappedComponent.getInitialProps) {
    WithAuthComponent.getInitialProps = WrappedComponent.getInitialProps;
  }

  WithAuthComponent.displayName = `withAuth(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithAuthComponent;
};
